import {
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';

import { VectorBanner } from '@/core/components/VectorBanner';
import { Breadcrumbs } from '@/core/components/Breadcrumbs';
import { OptionType } from '@/core/components/Select/SelectDefault';
import { useQueryParams } from '@/core/components/hooks/useQueryParams';
import { Pagination } from '@/core/components/Pagination';
import { Color } from '@/core/interfaces/common';
import { navigationMessages } from '@/core/messages/navigation.messages';
import { useLocalizedOGTitle } from '@/core/hooks/useLocalizedOGTitle';
import { globalMessages } from '@/core/messages/global.messages';

import { getKeywordsProps } from '@/utils/helpers';

import {
  ContentWrapper,
  PaginationWrapper,
  SelectsWrapper,
  StyledSelect,
} from './Articles.styles';
import { api } from './api';
import { ArticlesGrid } from './ArticlesGrid/ArticlesGrid';
import {
  ArticleDataResponse,
  ArticlePageResponse,
  ArticleTopicsResponse,
} from './Articles.types';

const sortingOptions = [
  {
    label: 'Newest first',
    value: 'DESC',
  },
  {
    label: 'Oldest first',
    value: 'ASC',
  },
];

export type ArticleProps = {
  articleResponseData: ArticleDataResponse;
  pageData: ArticlePageResponse;
  topicData: ArticleTopicsResponse;
};

const getSelectedOrDefaultValue = (
  array: typeof sortingOptions,
  queryParam: string
) => {
  const filteredOptions = array.filter(obj => obj.value === queryParam);

  return filteredOptions[0] || array[0];
};

export const Articles = ({
  articleResponseData,
  pageData,
  topicData,
}: ArticleProps) => {
  const [
    articleData,
    setArticleData,
  ] = useState(articleResponseData);
  const {
    addQueryParams,
    getQueryParams,
    removeQueryParam,
  } = useQueryParams();
  const { formatMessage } = useIntl();
  const { locale } = useRouter();

  const topicQueryParam = getQueryParams('topic');
  const sortingQueryParam = getQueryParams('sorting');
  const pageQueryParam = getQueryParams('page');

  const page = pageQueryParam ?? '1';
  const parsedPage = Array.isArray(page) ?
    parseInt(page[0], 10) :
    parseInt(page, 10);

  const options = [
    {
      label: 'All topics',
      value: '',
    },
    ...topicData?.data?.map(item => ({
      label: item.attributes.name,
      value: item.attributes.name,
    })),
  ];

  const methods = useForm({
    defaultValues: {
      sorting: getSelectedOrDefaultValue(
        sortingOptions,
        sortingQueryParam as string
      ),
      topic: getSelectedOrDefaultValue(options, topicQueryParam as string),
    },
  });

  const {
    setValue,
    watch,
    getValues,
  } = methods;

  const values = getValues();

  const handleOnChange = async (
    singleOptionObject: OptionType,
    id: 'topic' | 'sorting'
  ) => {
    setValue(id, singleOptionObject);

    addQueryParams({ [id]: singleOptionObject.value });
  };

  useEffect(() => {
    removeQueryParam('page');
    setValue('topic',
      {
        label: 'All topics',
        value: '',
      });
  }, [locale]);

  useEffect(() => {
    removeQueryParam('page');
  }, [values.sorting.value]);

  useEffect(() => {
    if (!values.topic.value) {
      removeQueryParam('topic');
    }
  }, [values.topic.value]);

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await api.getArticles({
          ...(values.topic.value && {
            'filters[topics][name]': values.topic.value,
          }),
          ...(values.sorting.value && {
            'sort[createdAt]': values.sorting.value,
          }),
          locale,
          'pagination[page]': parsedPage,
          'pagination[pageSize]': 8,
        });

        return setArticleData(res.data);
      } catch (error) {
        return null;
      }
    };

    getData();
  }, [
    parsedPage,
    values.sorting.value,
    values.topic.value,
  ]);

  const handlePageChange = (newPage: number) => {
    addQueryParams({ page: newPage });
  };

  const OGTitle = useLocalizedOGTitle(
    pageData.data.attributes.meta?.OGTitle ||
    pageData.data.attributes.meta?.title ||
    formatMessage(navigationMessages.vision)
  );

  return (
    <>
      <NextSeo
        title={pageData.data.attributes.meta?.title || formatMessage(navigationMessages.vision)}
        description={pageData.data.attributes.meta?.description || ''}
        {...getKeywordsProps(pageData.data.attributes.meta?.keywords)}
        openGraph={{
          description: pageData.data.attributes.meta?.OGDescription ||
            pageData.data.attributes.meta?.description ||
            '',
          title: OGTitle,
        }}
      />
      <VectorBanner
        header={pageData?.data?.attributes?.banner?.header}
        subheader={pageData?.data?.attributes?.banner?.paragraph}
        color={pageData?.data?.attributes?.banner?.backgroundColor || Color.BLUE}
      />
      <Breadcrumbs />
      <ContentWrapper>
        <SelectsWrapper>
          <FormProvider {...methods}>
            <StyledSelect
              id="topic"
              options={options}
              onChange={e => handleOnChange(e as OptionType, 'topic')}
              value={watch('topic')}
              aria-label={formatMessage(globalMessages.selectTopic)}
            />

            <StyledSelect
              id="sorting"
              options={sortingOptions}
              onChange={e => handleOnChange(e as OptionType, 'sorting')}
              value={watch('sorting')}
              aria-label={formatMessage(globalMessages.selectSortOption)}
            />
          </FormProvider>
        </SelectsWrapper>
        <ArticlesGrid ArticleData={articleData} />
      </ContentWrapper>
      {articleData.meta.pagination && (
        <PaginationWrapper>
          <Pagination
            currentPage={articleData.meta.pagination.page}
            pageSize={articleData.meta.pagination.pageSize}
            total={articleData.meta.pagination.total}
            totalPageCount={articleData.meta.pagination.pageCount}
            onPageChange={handlePageChange}
          />
        </PaginationWrapper>
      )}
    </>
  );
};
